/**
 * TypingIndicator Component
 *
 * This component displays an animated typing indicator with three dots, commonly used in chat applications.
 *
 * Props:
 * @param {string} [color="#000"] - The color of the dots.
 * @param {number} [speed=0.6] - The speed of the animation in seconds.
 * @param {number} [width=4] - The width of each dot in pixels.
 * @param {number} [height=4] - The height of each dot in pixels.
 */

import { Box } from '@chakra-ui/react';

function TypingIndicator({
  color = '#000',
  speed = 0.6,
  width = 4,
  height = 4,
}: {
  color?: string;
  speed?: number;
  width?: number;
  height?: number;
}) {
  const dots = ['dot1', 'dot2', 'dot3'];

  const durationMultiplier = 1 / Math.max(speed, 0.1);

  return (
    <Box display="flex" alignItems="center" height="18px">
      {dots.map((dot, index) => (
        <Box
          className="typing-indicator-dot"
          key={dot}
          sx={{
            backgroundColor: color,
            animation: `typingAnimation ${1.2 * durationMultiplier}s infinite ease-in-out`,
            animationDelay: `${200 + index * 100}ms`,
            width: `${width}px`,
            height: `${height}px`,
            borderRadius: 'full',
            display: 'inline-block',
            marginRight: '2px',
          }}
        ></Box>
      ))}
      <style>{`
        @-webkit-keyframes typingAnimation {
          0% {
            -webkit-transform: translateY(0px);
          }
          44% {
            -webkit-transform: translateY(-5px);
          }
          88% {
            -webkit-transform: translateY(0px);
          }
        }

        @keyframes typingAnimation {
          0% {
            transform: translateY(0px);
          }
          44% {
            transform: translateY(-5px);
          }
          88% {
            transform: translateY(0px);
          }
        }

        .typing-indicator-dot:nth-child(1) {
          -webkit-animation-delay: 200ms;
          animation-delay: 200ms;
        }
        .typing-indicator-dot:nth-child(2) {
          -webkit-animation-delay: 400ms;
          animation-delay: 400ms;
        }
        .typing-indicator-dot:nth-child(3) {
          -webkit-animation-delay: 600ms;
          animation-delay: 600ms;
        }
      `}</style>
    </Box>
  );
}

export default TypingIndicator;
